/**
 * Timestamp Utility
 * Convert datetime to relative time (e.g., "2 hours ago", "3 days ago")
 * with hover tooltip showing absolute datetime
 */

import { FormBase } from "../../util/form-base-form";
import { phpAppContext, phpClientContextUrl, phpUserLocale, phpUserTimezone } from "../php-data-access";
import { timestampApi } from "./timestamp-api";
import { TimestampListener } from "./timestamp-listener";

class TimestampFormBase extends FormBase {
    public getRelativeTimestampElements(root: ParentNode = document): HTMLElement[] {
        return this.queryElements(root, ".timestamp-relative", HTMLElement);
    }
}

const timestampFormBase = new TimestampFormBase();

type SupportedLanguage = "th" | "en" | "ja";
type RelativeTimeUnit = "minute" | "hour" | "day" | "week" | "month" | "year";
type TranslationKey = "just_now" | "yesterday" | "today";

interface TimestampLastLogged {
    lang: SupportedLanguage | null;
    stamp: number;
}

interface TimestampLanguageChangedDetail {
    language?: string;
}

interface TimestampContextState {
    locale?: string;
    timezone?: string;
    _lastSent?: {
        locale?: string;
        timezone?: string | null;
    };
}

type LanguageCodeInput = string | null | undefined;

interface RelativeTimeOptions {
    shortEnglishMobile?: boolean;
}

class TimestampHelper {
    public lang: SupportedLanguage;

    public timeZone: string | null;

    private _lastLogged: TimestampLastLogged;

    private shortEnglishMobileMediaQuery: MediaQueryList | null;

    public constructor() {
        this.lang = this.normalizeLanguageCode(
            localStorage.getItem("language") || document.documentElement.lang || "th",
        );
        this.timeZone = this.getBrowserTimeZone();
        this._lastLogged = { lang: null, stamp: 0 };
        this.shortEnglishMobileMediaQuery = null;
        this.init();
    }

    public normalizeLanguageCode(value: LanguageCodeInput, source = "unknown"): SupportedLanguage {
        const allowed: SupportedLanguage[] = ["th", "en", "ja"];
        if (typeof value === "string" && allowed.includes(value as SupportedLanguage)) {
            return value as SupportedLanguage;
        }
        if (value !== null && value !== undefined && value !== "") {
            console.error("[Timestamp] Invalid language code; falling back:", {
                source,
                value,
                fallback: "th",
            });
        }
        return "th";
    }

    public init(): void {
        this.syncClientContext();
        this.updateAllTimestamps();
        this.bindViewportChange();

        TimestampListener.bindLanguageChanged((event: Event) => {
            const detail =
                event instanceof CustomEvent ? (event.detail as TimestampLanguageChangedDetail | null) : null;
            const nextLang = detail?.language;
            if (typeof nextLang === "string" && nextLang) {
                this.lang = this.normalizeLanguageCode(nextLang, "languageChanged");
                document.documentElement.lang = nextLang;
                this.syncClientContext();
                this.updateAllTimestamps();
            }
        });

        setInterval(() => this.updateAllTimestamps(), 60000);
    }

    private bindViewportChange(): void {
        TimestampListener.bindViewportChange(() => this.updateAllTimestamps());

        if (typeof globalThis.matchMedia !== "function") {
            return;
        }

        this.shortEnglishMobileMediaQuery = globalThis.matchMedia("(max-width: 768px)");
        TimestampListener.bindMediaQueryChange(this.shortEnglishMobileMediaQuery, () => this.updateAllTimestamps());
    }

    public updateAllTimestamps(): void {
        const elements = timestampFormBase.getRelativeTimestampElements(document);
        const now = Date.now();
        const shouldLogThisCycle = this._lastLogged.lang !== this.lang || now - this._lastLogged.stamp > 60_000;
        if (shouldLogThisCycle) {
            this._lastLogged = { lang: this.lang, stamp: now };
        }

        const tooltipSamplesLogged = 0;

        elements.forEach((element) => {
            const datetime = element.getAttribute("data-timestamp");
            if (datetime && element instanceof HTMLElement) {
                const relativeTime = this.getRelativeTime(datetime, {
                    shortEnglishMobile: this.shouldUseShortEnglishMobile(element),
                });
                element.textContent = relativeTime;

                const title = this.formatAbsolute(datetime);
                element.title = title;

                if (shouldLogThisCycle && tooltipSamplesLogged < 3) {
                    // Reserved for future debug sampling.
                }

                element.style.opacity = "1";
            }
        });
    }

    public logLanguageAndTooltipContext(reason: string): void {
        try {
            console.log("[Timestamp] context:", {
                reason,
                selectedLanguage: this.lang,
                localStorageLanguage: localStorage.getItem("language"),
                documentLang: document.documentElement.lang,
                appContextLocale: phpUserLocale(),
                appContextTimezone: phpUserTimezone(),
                intlLocale: this.getIntlLocale(),
                timeZone: this.getTimeZone(),
            });
        } catch (_error) {
            // Non-blocking
        }
    }

    public getIntlLocale(): string {
        const map: Record<SupportedLanguage, string> = {
            th: "th-TH",
            en: "en-US",
            ja: "ja-JP",
        };

        const lang = this.normalizeLanguageCode(this.lang, "TimestampHelper.lang");

        const ctxLocaleRaw = phpUserLocale();
        const ctxLocale = this.normalizeLanguageCode(ctxLocaleRaw, "AppContext.locale");
        if (ctxLocaleRaw && ctxLocale !== lang) {
            console.error("[Timestamp] Locale mismatch (AppContext.locale vs selected language):", {
                selectedLanguage: lang,
                appContextLocaleRaw: ctxLocaleRaw,
                appContextLocaleNormalized: ctxLocale,
                intlLocaleSelected: map[lang] || null,
            });
        }

        return map[lang] || map.th;
    }

    public getBrowserTimeZone(): string | null {
        try {
            return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
        } catch (_error) {
            return null;
        }
    }

    public getTimeZone(): string | null {
        const timezone = phpUserTimezone();
        if (typeof timezone === "string" && timezone) return timezone;
        return this.timeZone || this.getBrowserTimeZone();
    }

    public async syncClientContext(): Promise<void> {
        const url = phpClientContextUrl();
        if (!url) return;

        const timezone = this.getBrowserTimeZone();
        const locale = this.normalizeLanguageCode(this.lang, "TimestampHelper.lang");

        const context = phpAppContext() as TimestampContextState | null;
        const lastSent = context?._lastSent || {};
        if (lastSent.locale === locale && lastSent.timezone === timezone) return;
        if (context) {
            context._lastSent = { locale, timezone };
            context.locale = locale;
            if (timezone) context.timezone = timezone;
        }

        try {
            const payload = await timestampApi.syncClientContext(url, { locale, timezone });
            const next = phpAppContext() as TimestampContextState | null;
            if (payload?.timezone && next) next.timezone = payload.timezone;
            if (payload?.locale && next) next.locale = payload.locale;
        } catch (_error) {
            // Non-blocking
        }
    }

    public toDate(dateTimeString: string | null | undefined): Date | null {
        if (!dateTimeString) return null;

        let normalized = dateTimeString;
        if (normalized.includes(" ") && !normalized.includes("T")) {
            normalized = normalized.replace(" ", "T");
        }

        const date = new Date(normalized);
        if (Number.isNaN(date.getTime())) return null;
        return date;
    }

    public formatAbsolute(dateTimeString: string | null | undefined): string {
        const date = this.toDate(dateTimeString);
        if (!date) return dateTimeString || "";

        const locale = this.getIntlLocale();
        const timeZone = this.getTimeZone();
        try {
            return new Intl.DateTimeFormat(locale, {
                timeZone: timeZone || undefined,
                year: "numeric",
                month: "short",
                day: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
            }).format(date);
        } catch (_error) {
            return date.toLocaleString();
        }
    }

    public shouldUseShortEnglishMobile(element: HTMLElement): boolean {
        if (this.lang !== "en") return false;
        if (element.dataset.shortEnMobile !== "true") return false;

        try {
            return window.matchMedia(`(max-width: 768px)`).matches;
        } catch (_error) {
            return false;
        }
    }

    public getRelativeTime(dateTimeString: string | null | undefined, options: RelativeTimeOptions = {}): string {
        if (!dateTimeString) return "";

        const date = this.toDate(dateTimeString) || new Date(dateTimeString);
        const now = new Date();
        const diffMs = now.getTime() - date.getTime();
        const diffSecs = Math.floor(diffMs / 1000);
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);
        const diffWeeks = Math.floor(diffMs / 604800000);
        const diffMonths = Math.floor(diffMs / 2592000000);
        const diffYears = Math.floor(diffMs / 31536000000);

        if (diffMs < 0) {
            return this.formatFuture(Math.abs(diffMs));
        }
        if (diffSecs < 60) {
            return this.translate("just_now", options);
        }
        if (diffMins < 60) {
            return this.formatTime(diffMins, "minute", options);
        }
        if (diffHours < 24) {
            return this.formatTime(diffHours, "hour", options);
        }

        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        if (this.isSameDay(date, yesterday)) {
            return this.translate("yesterday", options);
        }
        if (diffDays < 7) {
            return this.formatTime(diffDays, "day", options);
        }
        if (diffWeeks < 4) {
            return this.formatTime(diffWeeks, "week", options);
        }
        if (diffMonths < 12) {
            return this.formatTime(diffMonths, "month", options);
        }

        return this.formatTime(diffYears, "year", options);
    }

    public formatTime(value: number, unit: RelativeTimeUnit, options: RelativeTimeOptions = {}): string {
        if (options.shortEnglishMobile && this.lang === "en") {
            const compactUnits: Record<RelativeTimeUnit, string> = {
                minute: "m",
                hour: "h",
                day: "d",
                week: "w",
                month: "mo",
                year: "y",
            };

            return `${value}${compactUnits[unit]}`;
        }

        const translations: Record<SupportedLanguage, Record<RelativeTimeUnit, string>> = {
            th: {
                minute: `${value} นาทีที่แล้ว`,
                hour: `${value} ชั่วโมงที่แล้ว`,
                day: `${value} วันที่แล้ว`,
                week: `${value} สัปดาห์ที่แล้ว`,
                month: `${value} เดือนที่แล้ว`,
                year: `${value} ปีที่แล้ว`,
            },
            en: {
                minute: `${value} minute${value > 1 ? "s" : ""} ago`,
                hour: `${value} hour${value > 1 ? "s" : ""} ago`,
                day: `${value} day${value > 1 ? "s" : ""} ago`,
                week: `${value} week${value > 1 ? "s" : ""} ago`,
                month: `${value} month${value > 1 ? "s" : ""} ago`,
                year: `${value} year${value > 1 ? "s" : ""} ago`,
            },
            ja: {
                minute: `${value}分前`,
                hour: `${value}時間前`,
                day: `${value}日前`,
                week: `${value}週間前`,
                month: `${value}ヶ月前`,
                year: `${value}年前`,
            },
        };

        return translations[this.lang]?.[unit] || translations.en[unit];
    }

    public formatFuture(diffMs: number): string {
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 60) {
            return this.lang === "th"
                ? `ใน ${diffMins} นาที`
                : this.lang === "ja"
                  ? `${diffMins}分後`
                  : `in ${diffMins} minute${diffMins > 1 ? "s" : ""}`;
        }

        if (diffHours < 24) {
            return this.lang === "th"
                ? `ใน ${diffHours} ชั่วโมง`
                : this.lang === "ja"
                  ? `${diffHours}時間後`
                  : `in ${diffHours} hour${diffHours > 1 ? "s" : ""}`;
        }

        return this.lang === "th"
            ? `ใน ${diffDays} วัน`
            : this.lang === "ja"
              ? `${diffDays}日後`
              : `in ${diffDays} day${diffDays > 1 ? "s" : ""}`;
    }

    public translate(key: TranslationKey, options: RelativeTimeOptions = {}): string {
        if (options.shortEnglishMobile && this.lang === "en") {
            const compactTranslations: Partial<Record<TranslationKey, string>> = {
                just_now: "now",
                yesterday: "1d",
            };

            const compact = compactTranslations[key];
            if (compact) return compact;
        }

        const translations: Record<SupportedLanguage, Record<TranslationKey, string>> = {
            th: {
                just_now: "เมื่อสักครู่",
                yesterday: "เมื่อวาน",
                today: "วันนี้",
            },
            en: {
                just_now: "Just now",
                yesterday: "Yesterday",
                today: "Today",
            },
            ja: {
                just_now: "たった今",
                yesterday: "昨日",
                today: "今日",
            },
        };

        return translations[this.lang]?.[key] || translations.en[key];
    }

    public isSameDay(date1: Date, date2: Date): boolean {
        return (
            date1.getFullYear() === date2.getFullYear() &&
            date1.getMonth() === date2.getMonth() &&
            date1.getDate() === date2.getDate()
        );
    }
}

export const timestampHelper = new TimestampHelper();
